/**
 * ProjectUpdatesPeekDialog — R68.
 * Small popup that shows the most recent project status updates. Used
 * from the Portfolio dashboard so an exec can peek at a project's last
 * few updates without navigating to the project detail page.
 */
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Loader2, ExternalLink, Activity } from "lucide-react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { SmartRichTextDisplay } from "@/components/SmartRichText";

const HEALTH_COLOR = {
  on_track: "border-emerald-500 text-emerald-800",
  at_risk: "border-amber-500 text-amber-800",
  off_track: "border-rose-500 text-rose-800",
  blocked: "border-neutral-800 text-neutral-800",
  unset: "border-neutral-300 text-neutral-500",
};

function _fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ProjectUpdatesPeekDialog({ open, onOpenChange, projectId, projectName }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!open || !projectId) { setRows(null); return; }
    api.get(`/projects/${projectId}/status-updates`, { params: { limit: 20 } })
      .then((r) => setRows(r.data?.rows || r.data || []))
      .catch((e) => {
        setRows([]);
        toast.error(apiErrorMessage(e, "Updates fetch failed"));
      });
  }, [open, projectId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col rounded-none"
        data-testid="project-updates-peek-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Activity className="w-4 h-4" />
            <span className="truncate">Recent updates · {projectName || "project"}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto border">
          {rows === null && (
            <div className="p-8 text-center text-xs text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
            </div>
          )}
          {rows && rows.length === 0 && (
            <div className="p-8 text-center text-xs text-muted-foreground italic">
              No status updates yet for this project.
            </div>
          )}
          <ul className="divide-y">
            {(rows || []).map((u, i) => (
              <li key={u.id || i} className="p-3" data-testid={`project-update-peek-row-${i}`}>
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-1">
                  <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${HEALTH_COLOR[u.health || "unset"]}`}>
                    {u.health || "unset"}
                  </span>
                  <span>{u.author_name || u.author_email || "—"}</span>
                  <span className="ml-auto">{_fmtDate(u.created_at)}</span>
                </div>
                {u.body_html ? (
                  <SmartRichTextDisplay html={u.body_html} />
                ) : (
                  <div className="text-xs whitespace-pre-wrap">{u.body || u.summary || ""}</div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter className="shrink-0 pt-2 border-t gap-2">
          {projectId && (
            <Link
              to={`/app/projects/${projectId}?tab=updates`}
              className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mr-auto"
              data-testid="project-updates-peek-open"
            >
              Open project updates <ExternalLink className="w-3 h-3" />
            </Link>
          )}
          <Button variant="outline" size="sm" className="rounded-none"
                     onClick={() => onOpenChange?.(false)}
                     data-testid="project-updates-peek-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
